import {
  BookingDetails,
  BookingDetailsOrderItem,
  BookingItem,
  BookingType,
  ReservedDate,
} from "../pages/BookingsPage/bookingsSlice";
import { format } from "date-fns";

export const weekdaysInFinnish = ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"];
export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
export function getFinnishWeekday(date: Date): string {
  const weekdaysInFinnish = ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"];
  const dayIndex = date.getDay();
  return weekdaysInFinnish[dayIndex];
}

export function areDatesDateComponentsSame(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
export function dateToDateOnlyString(date: Date | string) {
  // Convert to Date if it's not already an instance of Date
  const parsedDate = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsedDate);
}

export function dateToDateOnlyStringTwoYear(date: Date | string) {
  // Convert to Date if it's not already an instance of Date
  const parsedDate = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(parsedDate);
}

export function JsDateToString(
  date: Date,
  showDay: boolean,
  showMonth: boolean,
  showYear: boolean,
  timeSeparator: string,
  showHours: boolean,
  showMinutes: boolean,
) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  // Build Intl options dynamically based on the function parameters
  const options: Intl.DateTimeFormatOptions = {};

  if (showDay) options.day = "numeric";
  if (showMonth) options.month = "numeric";
  if (showYear) options.year = "numeric";

  if (showHours) options.hour = "numeric";
  if (showMinutes) options.minute = "2-digit";

  // Easiest way to insert custom delimiter (" klo ") is to format pieces separately
  const formatter = new Intl.DateTimeFormat("fi-FI", options);

  let formatted = formatter.format(date);

  // If user wants a custom time separator, we patch it in
  if ((showHours || showMinutes) && timeSeparator) {
    // Finnish locale uses comma between date and time -> replace it
    formatted = formatted.replace(",", timeSeparator);
  }

  return formatted;
}

export function getAccommodationTranslationItem(locale, keyId, item) {
  if (
    item !== undefined &&
    item !== null &&
    item.translations !== null &&
    item.translations !== undefined
  ) {
    //console.log(item.translations);
    const found = item.translations.filter(
      (a) => a.locale === locale && a.keyId === keyId,
    );
    if (found && found[0] && found[0].text) {
      return found[0].text;
    }
  }

  return "";
}

function getAccommodationItemsFromBooking(booking: BookingDetails) {
  return (
    booking.bookingOrderItems?.filter(
      (i) => i.accommodationId && i.accommodationId !== EMPTY_GUID,
    ) ?? []
  );
}

function getEarliestStartDate(
  items: BookingDetailsOrderItem[],
  selector: (i: BookingDetailsOrderItem) => string,
) {
  return items.reduce((earliest: Date | undefined, item) => {
    const d = new Date(selector(item));
    return earliest === undefined || d < earliest ? d : earliest;
  }, undefined);
}

function getLatestEndDate(
  items: BookingDetailsOrderItem[],
  selector: (i: BookingDetailsOrderItem) => string,
) {
  return items.reduce((latest: Date | undefined, item) => {
    const d = new Date(selector(item));
    return latest === undefined || d > latest ? d : latest;
  }, undefined);
}
export function getStartTimeFromBooking(booking: BookingDetails) {
  const accommodationItems = getAccommodationItemsFromBooking(booking);

  // Main booking → use accommodation dates only
  if (accommodationItems.length > 0) {
    return getEarliestStartDate(accommodationItems, (x) => x.startDateTime);
  }

  // Extra booking → use all order items
  return getEarliestStartDate(
    booking.bookingOrderItems ?? [],
    (x) => x.startDateTime,
  );
}

export function getEndTimeFromBooking(booking: BookingDetails) {
  const accommodationItems = getAccommodationItemsFromBooking(booking);

  // Main booking → use accommodation dates only
  if (accommodationItems.length > 0) {
    return getLatestEndDate(accommodationItems, (x) => x.endDateTime);
  }

  // Extra booking → use all order items
  return getLatestEndDate(
    booking.bookingOrderItems ?? [],
    (x) => x.endDateTime,
  );
}

export function convertJSDateToDotNetDateTimeWithTime(date: Date) {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const monthString = month < 10 ? "0" + month : month.toString();
  const dayString = day < 10 ? "0" + day : day.toString();
  const hourString = hours < 10 ? "0" + hours : hours.toString();
  const minuteString = minutes < 10 ? "0" + minutes : minutes.toString();
  return (
    date.getFullYear().toString() +
    "-" +
    monthString +
    "-" +
    dayString +
    "T" +
    hourString +
    ":" +
    minuteString +
    ":00.000Z"
  );
}
export function convertJSDateToDotNetDateTimeDayStart(date: Date) {
  const month = date.getMonth() + 1;
  const monthString = month < 10 ? "0" + month : month.toString();
  const day = date.getDate();
  const dayString = day < 10 ? "0" + day : day.toString();
  return (
    date.getFullYear().toString() +
    "-" +
    monthString +
    "-" +
    dayString +
    "T" +
    "00:00:00.000Z"
  );
}
export function convertJSDateToDotNetDateTimeDayEnd(date: Date) {
  const month = date.getMonth() + 1;
  const monthString = month < 10 ? "0" + month : month.toString();
  const day = date.getDate();
  const dayString = day < 10 ? "0" + day : day.toString();
  return (
    date.getFullYear().toString() +
    "-" +
    monthString +
    "-" +
    dayString +
    "T" +
    "23:59:59.999Z"
  );
}
export function convertJSDateToShortDateString(date: Date) {
  const month = date.getMonth() + 1;
  let monthString: string = month.toString();
  let dayString: string = date.getDate().toString();
  return dayString + "." + monthString + "." + date.getFullYear().toString();
}
export function convertJSDateToShortDateStringWithDayOfWeek(date: Date) {
  const month = date.getMonth() + 1;
  let monthString: string = month.toString();
  let dayString: string = date.getDate().toString();
  return (
    getFinnishWeekday(date) +
    " " +
    dayString +
    "." +
    monthString +
    "." +
    date.getFullYear().toString()
  );
}
export function constructCalendarReservedDates(bookings: BookingItem[]) {
  const reservedDates: { [key: string]: ReservedDate } = {};

  function parseFinnishDateString(str: string): Date {
    // Expects dd.MM.yyyy
    const [dd, mm, yyyy] = str.split(".").map(Number);
    return new Date(yyyy, mm - 1, dd); // Always local midnight
  }

  function formatDateKey(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  bookings.forEach((booking) => {
    const startDate = parseFinnishDateString(booking.startDateString);
    const endDate = parseFinnishDateString(booking.endDateString);

    for (
      let d = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
      );
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const key = formatDateKey(normalized);

      const reservedDate: ReservedDate = {
        bookingId: booking.id,
        bookingType: booking.bookingType,
        startDay: normalized.getTime() === startDate.getTime(),
        endDay: normalized.getTime() === endDate.getTime(),
        adults: booking.adults,
        children: booking.children,
        ownerBookingNotes: booking.ownerBookingNotes,
        startDateString: booking.startDateString,
        endDateString: booking.endDateString,
        iCalDescription: booking.iCalDescription,
        iCalFullDescription: booking.iCalFullDescription,
        iCalLocation: booking.iCalLocation,
        iCalExternalUid: booking.iCalExternalUid,
        iCalImportId: booking.iCalImportId,
        iCalDistributionChannelName: booking.iCalDistributionChannelName,
        checkInTime: booking.checkInTime,
        checkOutTime: booking.checkOutTime,
      };

      if (reservedDates[key]) {
        // Merge if key already exists
        if (reservedDate.startDay) {
          reservedDates[key].startDay = true;
          reservedDates[key].bookingId = booking.id;
          reservedDates[key].bookingType = booking.bookingType;
          reservedDates[key].adults = booking.adults;
          reservedDates[key].children = booking.children;
          reservedDates[key].ownerBookingNotes = booking.ownerBookingNotes;
          reservedDates[key].startDateString = booking.startDateString;
          reservedDates[key].endDateString = booking.endDateString;
          reservedDates[key].iCalDescription = booking.iCalDescription;
          reservedDates[key].iCalExternalUid = booking.iCalExternalUid;
          reservedDates[key].iCalFullDescription = booking.iCalFullDescription;
          reservedDates[key].iCalLocation = booking.iCalLocation;
          reservedDates[key].iCalImportId = booking.iCalImportId;
          reservedDates[key].iCalDistributionChannelName =
            booking.iCalDistributionChannelName;
          reservedDates[key].checkInTime = booking.checkInTime;
          reservedDates[key].checkOutTime = booking.checkOutTime;
        }
        if (reservedDate.endDay) {
          reservedDates[key].endDay = true;
        }
      } else {
        reservedDates[key] = reservedDate;
      }
    }
  });

  return reservedDates;
}

export function constructCalendarReservedDatesDummy() {
  //let reservedDates= [];
  let reservedDates: { [key: string]: ReservedDate } = {};
  let startDate: Date = new Date();
  let endDate: Date = new Date();
  endDate.setDate(endDate.getDate() + 5);
  let currentDate = new Date(startDate);
  for (let d = currentDate; d <= endDate; d.setDate(d.getDate() + 1)) {
    //let currentDate = new Date(d);
    let reservedDate: ReservedDate = {
      bookingId: "",
      bookingType: 0,
      startDay: false,
      endDay: false,
    };
    if (d.getTime() === startDate.getTime()) {
      reservedDate.startDay = true;
    }
    if (currentDate.getTime() === endDate.getTime()) {
      reservedDate.endDay = true;
    }
    reservedDate.bookingId = "283d90ca-b706-4b6f-abc7-aa0da8124ea3";

    reservedDate.bookingType = 1;
    //reservedDates.push(reservedDate);
    let key: string = "";
    key += currentDate.getFullYear().toString() + "-";
    let keyMonth = currentDate.getMonth() + 1;
    key += keyMonth < 10 ? "0" + keyMonth.toString() : keyMonth.toString();
    key += "-";
    key +=
      currentDate.getDate() < 10
        ? "0" + currentDate.getDate().toString()
        : currentDate.getDate().toString();
    reservedDates[key] = reservedDate;

    //reservedDates.push({
    //  [key]: reservedDate,
    //});
  }

  return reservedDates;
}
export function constructCalendarHighlights(bookings: BookingItem[]) {
  let preliminaryStartBookingDates = [];
  let groupStartBookingDates = [];
  let internetStartBookingDates = [];
  let normalStartBookingDates = [];
  let ownerOwnStartBookingDates = [];
  let ownerReportedStartBookingDates = [];
  let temporarilyDisabledStartBookingDates = [];
  let reservedDates = [];
  bookings.map((booking) => {
    let startDate: Date = new Date(
      booking.startDateString.split(".")[1] +
        "-" +
        booking.startDateString.split(".")[0] +
        "-" +
        booking.startDateString.split(".")[2],
    );
    let endDate: Date = new Date(
      booking.endDateString.split(".")[1] +
        "-" +
        booking.endDateString.split(".")[0] +
        "-" +
        booking.endDateString.split(".")[2],
    );
    switch (booking.bookingType) {
      case BookingType.Preliminary:
      case BookingType.PreliminaryGroup:
        preliminaryStartBookingDates.push(startDate);
        break;
      case BookingType.ConfirmedGroup:
        groupStartBookingDates.push(startDate);
        break;
      case BookingType.Normal:
      case BookingType.TravelAgency:
        normalStartBookingDates.push(startDate);
        break;
      case BookingType.ConfirmedInternet:
      case BookingType.ConfirmedInternetOwnerSite:
      case BookingType.CONFIRMEDINTERHOME:
        internetStartBookingDates.push(startDate);
        break;
      case BookingType.OwnerOwn:
        ownerOwnStartBookingDates.push(startDate);
        break;
      case BookingType.OwnerReported:
        ownerReportedStartBookingDates.push(startDate);
        break;
      case BookingType.TemporaryDisabled:
        temporarilyDisabledStartBookingDates.push(startDate);
        break;
    }
    let loop = new Date(startDate);
    while (loop <= endDate) {
      let newDate = loop.setDate(loop.getDate() + 1);
      loop = new Date(newDate);
      reservedDates.push(newDate);
    }
  });
  let highlightWithRangesForBookings = [];
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--a":
      preliminaryStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--r":
      groupStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--x":
      normalStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--i":
      internetStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--o":
      ownerOwnStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--y":
      ownerReportedStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved react-datepicker__legend react-datepicker__legend--v":
      temporarilyDisabledStartBookingDates,
  });
  highlightWithRangesForBookings.push({
    "react-datepicker__day--reserved": reservedDates,
  });
  return highlightWithRangesForBookings;
}
export function parseFinnishDateString(str: string): Date {
  const [dd, mm, yyyy] = str.split(".").map(Number);
  return new Date(yyyy, mm - 1, dd); // local midnight
}
export function differenceBetweenDatesAsDaysInteger(
  startDate: Date,
  endDate: Date,
) {
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (24 * 3600 * 1000),
  );
}

export function bookingTypeEnumToTranslatedText(bookingType: BookingType) {
  switch (bookingType) {
    case BookingType.Preliminary:
      return "Alustava varaus";
    case BookingType.Normal:
      return "Normaali varaus";
    case BookingType.TravelAgency:
      return "Matkatoimisto varaus";
    case BookingType.UnConfirmedInternet:
      return "Vahvistamaton internet varaus";
    case BookingType.ConfirmedInternet:
      return "Internet varaus";
    case BookingType.OwnerOwn:
      return "Omistajan varaus";
    case BookingType.OwnerReported:
      return "Omistajan ilmoittama varaus";
    case BookingType.PreliminaryGroup:
      return "Alustava ryhmä varaus";
    case BookingType.ConfirmedGroup:
      return "Ryhmä varaus";
    case BookingType.UnConfirmedInternetOwnerSite:
      return "Vahvistamaton Internetvaraus (omat sivut)";
    case BookingType.ConfirmedInternetOwnerSite:
      return "Vahvistettu Internetvaraus (omat sivut)";
    case BookingType.UNCONFIRMEDINTERHOME:
      return "Vahvistamaton Interhome varaus";
    case BookingType.CONFIRMEDINTERHOME:
      return "Interhome varaus";
    case BookingType.TemporaryDisabled:
      return "Väliaikainen poisto";
    case BookingType.Ical:
      return "iCal -tapahtuma";
    case BookingType.None:
      return "Muu varaus";
  }
}
export function bookingTypeEnumToTranslatedShortHandText(
  bookingType: BookingType,
) {
  switch (bookingType) {
    case BookingType.Preliminary:
      return "A";
    case BookingType.Normal:
      return "N";
    case BookingType.TravelAgency:
      return "M";
    case BookingType.UnConfirmedInternet:
      return "AI";
    case BookingType.ConfirmedInternet:
      return "I";
    case BookingType.OwnerOwn:
      return "O";
    case BookingType.OwnerReported:
      return "OI";
    case BookingType.PreliminaryGroup:
      return "AR";
    case BookingType.ConfirmedGroup:
      return "R";
    case BookingType.UnConfirmedInternetOwnerSite:
      return "AOS";
    case BookingType.ConfirmedInternetOwnerSite:
      return "OS";
    case BookingType.UNCONFIRMEDINTERHOME:
      return "AI";
    case BookingType.CONFIRMEDINTERHOME:
      return "I";
    case BookingType.TemporaryDisabled:
      return "X";
    case BookingType.None:
      return "M";
  }
}
export const formattedAmountWithEuroCurrency = (amount: number) => {
  return new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formattedAmount = (amount: number) => {
  return new Intl.NumberFormat("fi-FI", {
    currency: "EUR",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDateToFinnish = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

export const showOrHideElementsByClassName = (
  className: string,
  show: boolean,
) => {
  const bookingNumberLinks = document.getElementsByClassName(className);
  // Loop through all elements and hide them
  for (let i = 0; i < bookingNumberLinks.length; i++) {
    const link = bookingNumberLinks[i] as HTMLElement; // Type assertion
    link.style.display = show ? "block" : "none";
  }
};

export const WEEKDAYS_SHORT = [
  {
    weekdayInt: 1,
    weekdayString: "Ma",
  },
  {
    weekdayInt: 2,
    weekdayString: "Ti",
  },
  {
    weekdayInt: 3,
    weekdayString: "Ke",
  },
  {
    weekdayInt: 4,
    weekdayString: "To",
  },
  {
    weekdayInt: 5,
    weekdayString: "Pe",
  },
  {
    weekdayInt: 6,
    weekdayString: "La",
  },
  {
    weekdayInt: 0,
    weekdayString: "Su",
  },
];

/** Round a number to 2 decimal places (cents) */
export const roundToCents = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;
